import React, { useState } from 'react';
import TopNav from '../components/TopNav';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { TrendingUp, Zap, DollarSign, Clock, CheckCircle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export default function InvestmentOpportunities() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    
    // Here you would typically send to your backend
    console.log('Investment interest:', email);
    toast.success('Thanks! We\'ll send you investment details shortly.');
    setSubmitted(true);
  };
  
  return (
    <div className="min-h-screen flex flex-col">
      <TopNav />
      
      <main className="flex-grow">
        <div className="bg-gradient-to-b from-muted/50 to-background py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <Badge className="mb-4" variant="secondary">Investment Opportunities</Badge>
              <h1 className="text-5xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Invest in Scalable Automation
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Get access to a portfolio of 95% automated businesses designed to reach cashflow positive in under 6 months.
              </p>
            </div>
            
            {/* Why Automation is a Great Investment */}
            <Card className="rounded-xl mb-8 bg-gradient-to-br from-primary/5 to-background border-2" style={{ borderColor: 'var(--ap-blue)' }}>
              <CardHeader>
                <CardTitle className="text-2xl">Why Automation is a Great Investment</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <DollarSign className="h-6 w-6" style={{ color: 'var(--ap-blue)' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Lower Operating Costs</h3>
                    <p className="text-sm text-muted-foreground">
                      Automated businesses run on margins traditional companies dream of. Less overhead, higher profitability.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <TrendingUp className="h-6 w-6" style={{ color: 'var(--ap-blue)' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Exponential Scalability</h3>
                    <p className="text-sm text-muted-foreground">
                      Scale revenue 10x without scaling costs 10x. Systems scale infinitely—people don't.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Clock className="h-6 w-6" style={{ color: 'var(--ap-blue)' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Faster Time to Profit</h3>
                    <p className="text-sm text-muted-foreground">
                      Our 6-month cashflow positive target means your capital starts working faster than traditional investments.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Zap className="h-6 w-6" style={{ color: 'var(--ap-blue)' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Predictable Performance</h3>
                    <p className="text-sm text-muted-foreground">
                      Automated systems produce consistent, measurable results. Less human error, more reliable growth.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Investment Thesis */}
            <Card className="rounded-xl mb-8">
              <CardHeader>
                <CardTitle className="text-2xl">Our Investment Thesis</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  <strong className="text-foreground">The future of business isn't "tech" vs "non-tech"—it's "automated" vs "manual."</strong>
                </p>
                <p>
                  Traditional businesses scale linearly. You need to hire 10 people to serve 10x customers. Automated businesses scale exponentially—10x customers might only require 2x the team (if any).
                </p>
                <p>
                  We invest in businesses that can be 95% automated and cashflow positive within 6 months. This aggressive timeline forces us to build efficient, lean operations from day one.
                </p>
                <p>
                  <strong className="text-foreground">The result?</strong> Higher margins, faster growth, and more predictable returns than traditional investments.
                </p>
              </CardContent>
            </Card>
            
            {/* What You're Investing In */}
            <Card className="rounded-xl mb-8">
              <CardHeader>
                <CardTitle className="text-2xl">What You're Investing In</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--ap-blue)' }} />
                    <p className="text-sm text-muted-foreground">
                      <strong className="text-foreground">A diversified portfolio</strong> of businesses we create, launch, or acquire
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--ap-blue)' }} />
                    <p className="text-sm text-muted-foreground">
                      <strong className="text-foreground">Proven automation frameworks</strong> applied to every company in our portfolio
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--ap-blue)' }} />
                    <p className="text-sm text-muted-foreground">
                      <strong className="text-foreground">Operational expertise</strong> from a team that's built and scaled automated businesses before
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--ap-blue)' }} />
                    <p className="text-sm text-muted-foreground">
                      <strong className="text-foreground">Aggressive timelines</strong> that ensure capital efficiency and fast path to profitability
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Email Capture CTA */}
            {!submitted ? (
              <Card className="rounded-xl bg-gradient-to-br from-primary/10 to-background border-2" style={{ borderColor: 'var(--ap-blue)' }}>
                <CardContent className="pt-8 pb-8">
                  <div className="text-center mb-6">
                    <h2 className="text-3xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      Interested in Learning More?
                    </h2>
                    <p className="text-muted-foreground max-w-xl mx-auto">
                      Enter your email below and we'll send you detailed information about current investment opportunities.
                    </p>
                  </div>
                  
                  <form onSubmit={handleSubmit} className="max-w-md mx-auto">
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="email" className="sr-only">Email Address</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="your.email@example.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="text-center text-lg h-12"
                          data-testid="investment-email-input"
                        />
                      </div>
                      <Button 
                        type="submit" 
                        size="lg" 
                        className="w-full rounded-xl"
                        data-testid="investment-submit-button"
                      >
                        Get Investment Details <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </form>
                  
                  <p className="text-xs text-center text-muted-foreground mt-4">
                    Investment opportunities are subject to qualification and availability.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card className="rounded-xl bg-gradient-to-br from-green-50 to-background dark:from-green-950/20 border-2 border-green-200 dark:border-green-900">
                <CardContent className="pt-8 pb-8 text-center">
                  <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-600 dark:text-green-500" />
                  <h3 className="text-2xl font-semibold mb-2">Thank You!</h3>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    We'll send detailed investment information to <strong>{email}</strong> shortly. Keep an eye on your inbox!
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
